import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api.ts';

interface Project {
  company: any;
  id: number;
  name: string;
  logo?: string;
  color_scheme?: {
    primary: string;
    secondary: string;
  };
  odoo?: {
    url: string;
    db: string;
    username: string;
    password?: string;
  };
  company_id?: any;
  odoo_configuration_id?: string;
}

interface User {
  id: number;
  name: string;
  email: string;
  role: string;
  token?: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  project: Project | null;
  login: (userData: User, projectData?: Project) => void;
  logout: () => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  user: null,
  project: null,
  login: () => {},
  logout: () => {},
  isLoading: true,
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [project, setProject] = useState<Project | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  // The Odoo session lives on the server now. Anything an earlier build left
  // in this browser is removed.
  useEffect(() => {
    localStorage.removeItem('odooSession');
    localStorage.removeItem('odooSessionExpiry');
  }, []);

  // Separate cleanup function to avoid circular dependencies
  const cleanupSession = () => {
    setUser(null);
    setProject(null);
    setIsAuthenticated(false);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('project');
    localStorage.removeItem('odooSession');
    localStorage.removeItem('odooSessionExpiry');
  };

  useEffect(() => {
    let isSubscribed = true;

    const verifyUser = async () => {
      const storedUser = localStorage.getItem('user');

      if (!storedUser) {
        if (isSubscribed) {
          setIsLoading(false);
          cleanupSession();
        }
        return;
      }

      try {
        if (isSubscribed) {
          setUser(JSON.parse(storedUser));
          setIsAuthenticated(true);

          const response = await authAPI.verifyUser();
          if (response.success && isSubscribed) {
            setUser(response.data.user);
            setProject(response.data.project);
            setIsAuthenticated(true);
          } else if (isSubscribed) {
            cleanupSession();
          }
        }
      } catch (error) {
        if (isSubscribed) {
          console.error('Session verification failed:', error);
          cleanupSession();
        }
      } finally {
        if (isSubscribed) {
          setIsLoading(false);
        }
      }
    };

    if (!isLoggingOut) {
      verifyUser();
    }

    return () => {
      isSubscribed = false;
    };
  }, [isLoggingOut]);

  const login = (responseData: any) => {
    // Response data already contains the structure we need
    const userData = {
      ...responseData.user,
      token: responseData.token,
    };
    const projectData = responseData.project;

    setUser(userData);
    setProject(projectData);
    setIsAuthenticated(true);

    // Store in localStorage
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('token', responseData.token);
    if (projectData) {
      localStorage.setItem('project', JSON.stringify(projectData));
    }
  };

  const logout = async () => {
    if (isLoggingOut) return;

    setIsLoggingOut(true);
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout API error:', error);
    } finally {
      cleanupSession();
      setIsLoggingOut(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        project,
        login,
        logout,
        isLoading,
      }}
    >
      {!isLoading ? children : null}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
